import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel,
  FormLabel, IconButton, InputLabel, ListItemText, MenuItem, Radio, RadioGroup, Select, Snackbar, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddOutlined, ChevronLeft, ChevronRight, ContentCopyOutlined, EventSeatOutlined, PaymentsOutlined, RefreshOutlined, WhatsApp,
} from '@mui/icons-material';
import { getApiBaseUrl } from '../kasir/productpercabang/apiProductPercabang';
import QrisKasir from '../kasir/QrisKasir';
import {
  DP_WARNA, STATUS_LABEL, STATUS_WARNA, aksiTersedia, geserTanggal, namaMejaReservasi, perluKeputusanDp, ringkasDp, rupiah, sekarangDiZona, tanggalPanjang, tautanWa,
} from '../../utils/reservasi';

const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true });
const pesanGalat = (e, cadangan) => e?.response?.data?.message || e?.response?.data?.msg || cadangan;

const DURASI = [60, 90, 120, 150, 180, 240];
const DURASI_MIN = 1; // sama dengan server (utils/reservasi.js)
const DURASI_MAKS = 1440;
const durasiSah = (d) => Number.isInteger(Number(d)) && Number(d) >= DURASI_MIN && Number(d) <= DURASI_MAKS && String(d) !== '';
const FORM_KOSONG = { customerUuid: '', nama: '', telepon: '', jumlahOrang: 2, tanggal: '', jam: '19:00', durasiMenit: 90, tableId: '', mejaTambahan: [], catatan: '', pakaiDp: false, dpNominal: '', dpMetode: 'cash', dpNonCash: '' };

/** Kolom pilihan keputusan DP (dikembalikan / hangus) yang dipakai di dialog tutup & keputusan. */
const PilihanKeputusanDp = ({ r, nilai, setNilai }) => (
  <FormControl sx={{ mt: 1 }}>
    <FormLabel>DP {rupiah(r.dp.nominal)} ({ringkasDp(r).match(/\((.*)\)/)?.[1] || ''}) diapakan?</FormLabel>
    <RadioGroup value={nilai} onChange={(e) => setNilai(e.target.value)}>
      <FormControlLabel value="kembali" control={<Radio />} label="Dikembalikan ke pelanggan" />
      <FormControlLabel value="hangus" control={<Radio />} label="Hangus (menjadi pendapatan cabang)" />
    </RadioGroup>
  </FormControl>
);

/**
 * Halaman Reservasi / booking meja cafe. Dipakai di web admin (di dalam Layout) dan di layar kasir (di dalam LayoutKasir).
 * Pembuat reservasi adalah staf; pelanggan hanya melihat halaman status lewat kode/tautan yang dikirim lewat WhatsApp.
 * DP (tunai / non-tunai / QRIS) dicatat di sini dan dipotong saat pembayaran di kasir.
 */
export const Reservasi = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const cabangSendiri = user?.cabang?.uuid || user?.cabanguuid || '';
  const [daftarCabang, setDaftarCabang] = useState([]);
  const [pilihCabang, setPilihCabang] = useState('');
  const cabangUuid = superadmin ? pilihCabang : cabangSendiri;

  const [tanggal, setTanggal] = useState(() => sekarangDiZona().tanggal);
  const [status, setStatus] = useState('semua');
  const [cari, setCari] = useState('');
  const [data, setData] = useState([]);
  const [ringkasan, setRingkasan] = useState(null);
  const [memuat, setMemuat] = useState(false);
  const [galat, setGalat] = useState('');
  const [pesan, setPesan] = useState(null);
  const [form, setForm] = useState(null); // { mode: 'baru'|'ubah', r }
  const [tutup, setTutup] = useState(null); // { jenis: 'batal'|'tidak-datang'|'selesai', r }
  const [dpDialog, setDpDialog] = useState(null); // r
  const [keputusan, setKeputusan] = useState(null); // r
  const [qris, setQris] = useState(null); // { r, qris_data }
  const [metodeNonTunai, setMetodeNonTunai] = useState([]);

  const lapor = (severity, message) => setPesan({ severity, message });
  const param = useMemo(() => (superadmin ? { cabang: cabangUuid } : {}), [superadmin, cabangUuid]);

  useEffect(() => {
    if (!superadmin) return;
    api.get('/cabang').then((r) => {
      const cafe = (r.data?.data || []).filter((c) => c.tipe_cabang === 'cafe');
      setDaftarCabang(cafe);
      setPilihCabang((lama) => lama || cafe[0]?.uuid || '');
    }).catch(() => setGalat('Gagal memuat daftar cabang.'));
  }, [superadmin]);

  useEffect(() => {
    api.get('/getnoncashmethod').then((r) => setMetodeNonTunai(Array.isArray(r.data?.data) ? r.data.data : [])).catch(() => setMetodeNonTunai([]));
  }, []);
  const nonTunaiCabang = useMemo(() => metodeNonTunai.filter((m) => !m.cabanguuid || m.cabanguuid === cabangUuid), [metodeNonTunai, cabangUuid]);

  const muat = useCallback(async (senyap = false) => {
    if (!cabangUuid) return;
    if (!senyap) setMemuat(true);
    try {
      const r = await api.get('/reservasi', { params: { ...param, tanggal, status, q: cari || undefined } });
      setData(r.data?.data || []);
      setRingkasan(r.data?.ringkasan || null);
      setGalat('');
    } catch (e) {
      setGalat(pesanGalat(e, 'Gagal memuat reservasi. Periksa koneksi ke server.'));
    } finally {
      if (!senyap) setMemuat(false);
    }
  }, [cabangUuid, param, tanggal, status, cari]);

  useEffect(() => { muat(); }, [muat]);
  useEffect(() => {
    const t = setInterval(() => muat(true), 30000);
    return () => clearInterval(t);
  }, [muat]);

  const aksi = async (r, jalur, body = {}, sukses = 'Reservasi diperbarui') => {
    try {
      const res = await api.post(`/reservasi/${r.uuid}/${jalur}`, { ...param, ...body });
      lapor('success', res.data?.petunjukDp ? `${sukses}. ${res.data.petunjukDp}` : sukses);
      await muat(true);
      return res.data;
    } catch (e) {
      lapor('error', pesanGalat(e, 'Gagal memperbarui reservasi'));
      return null;
    }
  };

  const salinTautan = async (r) => {
    try {
      await navigator.clipboard.writeText(r.tautanStatus);
      lapor('success', 'Tautan status disalin');
    } catch (e) {
      lapor('warning', r.tautanStatus);
    }
  };

  const kartu = (r) => {
    const a = aksiTersedia(r);
    const wa = tautanWa(r);
    return (
      <Card key={r.uuid} variant="outlined" data-testid={`reservasi-${r.kode}`} sx={{ opacity: ['batal', 'tidak_datang', 'selesai'].includes(r.status) ? 0.8 : 1 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{r.rentangJam || `${r.jam}–${r.jamSelesai}`}</Typography>
            <Chip size="small" label={STATUS_LABEL[r.status]} color={STATUS_WARNA[r.status]} />
            <Chip size="small" variant="outlined" label={ringkasDp(r)} color={DP_WARNA[r.dp.status]} data-testid="chip-dp" />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{r.kode}</Typography>
          </Stack>
          <Typography sx={{ mt: 0.5, fontWeight: 600 }}>{r.nama} · {r.jumlahOrang} orang{r.meja ? ` · Meja ${namaMejaReservasi(r)}` : ' · tanpa meja'}</Typography>
          {r.telepon && <Typography variant="body2" color="text.secondary">{r.telepon}</Typography>}
          {r.catatan && <Typography variant="body2" sx={{ mt: 0.5 }}>Catatan: {r.catatan}</Typography>}
          {r.alasanBatal && <Typography variant="body2" color="text.secondary">Alasan: {r.alasanBatal}</Typography>}
          {r.dp.catatan && <Typography variant="body2" color="warning.main">{r.dp.catatan}</Typography>}
          {a.dpBisaDipakai && (
            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
              DP {rupiah(r.dp.nominal)} dipotong saat pembayaran: pilih reservasi ini di layar pembayaran kasir.
            </Alert>
          )}
          {r.dp.status === 'dipakai' && (
            <Typography variant="body2" color="text.secondary">DP terpakai {rupiah(r.dp.terpakai)}{r.dp.kembali > 0 ? `, sisa ${rupiah(r.dp.kembali)} dikembalikan` : ''}.</Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
            {a.datang && <Button size="small" variant="contained" color="success" onClick={() => aksi(r, 'datang', {}, 'Tamu ditandai datang')}>Tamu datang</Button>}
            {a.selesai && <Button size="small" variant="contained" onClick={() => (perluKeputusanDp(r) ? setTutup({ jenis: 'selesai', r }) : aksi(r, 'selesai', {}, 'Reservasi selesai'))}>Selesai</Button>}
            {a.ubah && <Button size="small" variant="outlined" onClick={() => setForm({ mode: 'ubah', r })}>Ubah</Button>}
            {a.tambahDp && !a.lanjutQris && <Button size="small" variant="outlined" startIcon={<PaymentsOutlined />} onClick={() => setDpDialog(r)}>Tambah DP</Button>}
            {a.lanjutQris && <Button size="small" variant="outlined" color="warning" startIcon={<PaymentsOutlined />} onClick={() => setDpDialog(r)}>DP QRIS menunggu…</Button>}
            {a.keputusanDp && <Button size="small" variant="contained" color="warning" onClick={() => setKeputusan(r)}>Putuskan DP</Button>}
            {a.tidakDatang && <Button size="small" color="warning" onClick={() => setTutup({ jenis: 'tidak-datang', r })}>Tidak datang</Button>}
            {a.batal && <Button size="small" color="error" onClick={() => setTutup({ jenis: 'batal', r })}>Batalkan</Button>}
            {wa && (
              <Tooltip title="Kirim konfirmasi lewat WhatsApp">
                <Button size="small" component="a" href={wa} target="_blank" rel="noopener noreferrer" startIcon={<WhatsApp />} sx={{ color: '#128c7e' }}>WhatsApp</Button>
              </Tooltip>
            )}
            {r.tautanStatus && (
              <Tooltip title="Salin tautan status untuk pelanggan">
                <IconButton size="small" aria-label="Salin tautan status" onClick={() => salinTautan(r)}><ContentCopyOutlined fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 }, maxWidth: 1100 }}>
      <Stack direction="row" sx={{ alignItems: 'center', mb: 0.5 }} spacing={1}>
        <EventSeatOutlined color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>Reservasi</Typography>
        <Tooltip title="Muat ulang"><IconButton onClick={() => muat()} aria-label="Muat ulang"><RefreshOutlined /></IconButton></Tooltip>
        <Button variant="contained" startIcon={<AddOutlined />} disabled={!cabangUuid} onClick={() => setForm({ mode: 'baru', r: null })} data-testid="tombol-buat-reservasi">Reservasi</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Booking meja untuk tamu yang menelepon atau datang langsung. Kirim kode booking lewat WhatsApp; pelanggan bisa melihat statusnya lewat tautan.
      </Typography>

      {superadmin && (
        <FormControl size="small" sx={{ minWidth: 260, mb: 2 }}>
          <InputLabel>Cabang</InputLabel>
          <Select label="Cabang" value={pilihCabang} onChange={(e) => setPilihCabang(e.target.value)}>
            {daftarCabang.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      {!cabangUuid ? (
        <Alert severity="info">{superadmin ? 'Pilih cabang cafe untuk melihat reservasinya.' : 'Akun ini tidak terhubung ke cabang.'}</Alert>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2, alignItems: { sm: 'center' } }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <IconButton aria-label="Hari sebelumnya" onClick={() => setTanggal((t) => geserTanggal(t, -1))}><ChevronLeft /></IconButton>
              <TextField size="small" type="date" label="Tanggal" value={tanggal} onChange={(e) => e.target.value && setTanggal(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ 'data-testid': 'filter-tanggal' }} />
              <IconButton aria-label="Hari berikutnya" onClick={() => setTanggal((t) => geserTanggal(t, 1))}><ChevronRight /></IconButton>
              <Button size="small" onClick={() => setTanggal(sekarangDiZona().tanggal)}>Hari ini</Button>
            </Stack>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="semua">Semua</MenuItem>
                <MenuItem value="aktif">Aktif (belum selesai)</MenuItem>
                {Object.entries(STATUS_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" label="Cari nama / kode / telepon" value={cari} onChange={(e) => setCari(e.target.value)} sx={{ minWidth: 220 }} />
          </Stack>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>{tanggalPanjang(tanggal)}</Typography>
          {ringkasan && (
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
              <Chip size="small" label={`${ringkasan.total} reservasi`} />
              <Chip size="small" color="primary" variant="outlined" label={`${ringkasan.dikonfirmasi} menunggu datang`} />
              <Chip size="small" color="success" variant="outlined" label={`${ringkasan.datang} sudah datang`} />
              {ringkasan.batal + ringkasan.tidakDatang > 0 && <Chip size="small" color="warning" variant="outlined" label={`${ringkasan.batal + ringkasan.tidakDatang} batal/tidak datang`} />}
              {ringkasan.dpDiterima > 0 && <Chip size="small" color="success" label={`DP di tangan ${rupiah(ringkasan.dpDiterima)}`} />}
            </Stack>
          )}
          {galat && <Alert severity="error" sx={{ mb: 2 }}>{galat}</Alert>}
          {memuat && data.length === 0 ? <CircularProgress size={28} /> : (
            <Stack spacing={1.5}>
              {data.length === 0 && !galat && <Alert severity="info">Belum ada reservasi pada tanggal ini.</Alert>}
              {data.map(kartu)}
            </Stack>
          )}
        </>
      )}

      {form && (
        <FormReservasi
          mode={form.mode}
          r={form.r}
          tanggalAwal={tanggal}
          param={param}
          nonTunai={nonTunaiCabang}
          onTutup={() => setForm(null)}
          onSimpan={async (hasil) => {
            setForm(null);
            lapor(hasil.peringatan ? 'warning' : 'success', hasil.peringatan || hasil.message || 'Reservasi disimpan');
            if (hasil.qris_data) setQris({ r: hasil.data, qris_data: hasil.qris_data });
            await muat(true);
          }}
        />
      )}

      {tutup && (
        <DialogTutup
          jenis={tutup.jenis}
          r={tutup.r}
          onTutup={() => setTutup(null)}
          onKirim={async (body) => {
            const { jenis, r } = tutup;
            const sukses = { batal: 'Reservasi dibatalkan', 'tidak-datang': 'Ditandai tidak datang', selesai: 'Reservasi selesai' }[jenis];
            const hasil = await aksi(r, jenis, body, sukses);
            if (hasil) setTutup(null);
          }}
        />
      )}

      {dpDialog && (
        <DialogDp
          r={dpDialog}
          param={param}
          nonTunai={nonTunaiCabang}
          onTutup={() => setDpDialog(null)}
          onSelesai={async (hasil) => {
            setDpDialog(null);
            lapor('success', 'DP dicatat');
            if (hasil.qris_data) setQris({ r: hasil.data, qris_data: hasil.qris_data });
            await muat(true);
          }}
        />
      )}

      {keputusan && (
        <DialogKeputusan
          r={keputusan}
          onTutup={() => setKeputusan(null)}
          onKirim={async (k, catatan) => {
            try {
              const res = await api.post(`/reservasi/${keputusan.uuid}/dp/keputusan`, { ...param, keputusan: k, catatan });
              lapor('success', `DP diputuskan. ${res.data?.petunjukDp || ''}`.trim());
              setKeputusan(null);
              await muat(true);
            } catch (e) {
              lapor('error', pesanGalat(e, 'Gagal memutuskan DP'));
            }
          }}
        />
      )}

      {qris && (
        <DialogQris
          awal={qris}
          onTutup={async () => { setQris(null); await muat(true); }}
          onLunas={() => lapor('success', 'DP QRIS lunas')}
        />
      )}

      <Snackbar open={!!pesan} autoHideDuration={6000} onClose={() => setPesan(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {pesan ? <Alert severity={pesan.severity} onClose={() => setPesan(null)} sx={{ maxWidth: 560 }}>{pesan.message}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};

/** Isian DP (dipakai form buat & dialog tambah DP). */
const IsianDp = ({ nilai, setNilai, nonTunai }) => (
  <Stack spacing={1.5} sx={{ mt: 1 }}>
    <TextField label="Nominal DP" size="small" type="number" value={nilai.dpNominal} onChange={(e) => setNilai({ ...nilai, dpNominal: e.target.value })} InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>Rp</Typography> }} inputProps={{ min: 1, 'data-testid': 'dp-nominal' }} />
    <FormControl>
      <FormLabel>Dibayar dengan</FormLabel>
      <RadioGroup row value={nilai.dpMetode} onChange={(e) => setNilai({ ...nilai, dpMetode: e.target.value })}>
        <FormControlLabel value="cash" control={<Radio />} label="Tunai" />
        <FormControlLabel value="noncash" control={<Radio />} label="Non-tunai" disabled={nonTunai.length === 0} />
        <FormControlLabel value="qris" control={<Radio />} label="QRIS" />
      </RadioGroup>
    </FormControl>
    {nilai.dpMetode === 'noncash' && (
      <FormControl size="small">
        <InputLabel>Metode non-tunai</InputLabel>
        <Select label="Metode non-tunai" value={nilai.dpNonCash} onChange={(e) => setNilai({ ...nilai, dpNonCash: e.target.value })}>
          {nonTunai.map((m) => <MenuItem key={m.uuid} value={m.name}>{m.name}</MenuItem>)}
        </Select>
      </FormControl>
    )}
    {nilai.dpMetode === 'qris' && <Typography variant="caption" color="text.secondary">QRIS dibuat setelah disimpan; pelanggan memindai dari layar ini. Nominal harus bilangan bulat.</Typography>}
  </Stack>
);

const bentukDp = (n) => ({ nominal: Number(n.dpNominal), metode: n.dpMetode, ...(n.dpMetode === 'noncash' ? { nonCashMethodName: n.dpNonCash } : {}) });

/** Dialog buat / ubah reservasi. Pilihan meja menandai meja yang sudah terpakai pada jam itu. */
const FormReservasi = ({ mode, r, tanggalAwal, param, nonTunai, onTutup, onSimpan }) => {
  const baru = mode === 'baru';
  const [f, setF] = useState(() => (r ? {
    ...FORM_KOSONG, nama: r.nama, telepon: r.telepon || '', jumlahOrang: r.jumlahOrang, tanggal: r.tanggal, jam: r.jam, durasiMenit: r.durasiMenit, tableId: r.tableId || '', mejaTambahan: (r.mejaTambahan || []).map((m) => m.id), catatan: r.catatan || '',
  } : { ...FORM_KOSONG, tanggal: tanggalAwal }));
  const [meja, setMeja] = useState([]);
  // Durasi di luar pilihan bawaan = isian bebas (menit).
  const [durasiLain, setDurasiLain] = useState(() => !!r && !DURASI.includes(Number(r.durasiMenit)));
  const [kirim, setKirim] = useState(false);
  const [galat, setGalat] = useState('');
  const [pelanggan, setPelanggan] = useState([]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  // Nama pemesan bisa diambil dari data Customer: ketik minimal 2 huruf (nama / telepon), pilih, nama & telepon terisi sendiri.
  // Mengetik nama baru tetap boleh (pelanggan tidak harus terdaftar).
  useEffect(() => {
    const kata = f.nama.trim();
    if (!baru || kata.length < 2 || f.customerUuid) { setPelanggan([]); return undefined; }
    let batal = false;
    const t = setTimeout(() => {
      api.get('/getcustomerkasir', { params: { search: kata, ...(param.cabang ? { cabanguuid: param.cabang } : {}) } })
        .then((x) => {
          const semua = x.data?.data || [];
          const hanyaCabang = param.cabang ? semua.filter((c) => !c.Cabang || c.Cabang.uuid === param.cabang) : semua;
          if (!batal) setPelanggan(hanyaCabang.slice(0, 10));
        })
        .catch(() => { if (!batal) setPelanggan([]); });
    }, 300);
    return () => { batal = true; clearTimeout(t); };
  }, [f.nama, f.customerUuid, baru, param]);
  const waktuTetap = !baru && r?.status !== 'dikonfirmasi';

  useEffect(() => {
    if (!f.tanggal || !f.jam) return undefined;
    if (!durasiSah(f.durasiMenit)) { setMeja([]); return undefined; } // durasi belum sah: jangan tampilkan ketersediaan lama
    let batal = false;
    api.get('/reservasi/meja', { params: { ...param, tanggal: f.tanggal, jam: f.jam, durasiMenit: f.durasiMenit, jumlahOrang: f.jumlahOrang, kecuali: r?.uuid } })
      .then((x) => { if (!batal) setMeja(x.data?.data || []); })
      .catch(() => { if (!batal) setMeja([]); });
    return () => { batal = true; };
  }, [f.tanggal, f.jam, f.durasiMenit, f.jumlahOrang, param, r]);

  const simpan = async () => {
    setKirim(true);
    setGalat('');
    try {
      const isi = {
        ...param,
        nama: f.nama,
        telepon: f.telepon,
        ...(baru && f.customerUuid ? { customerUuid: f.customerUuid } : {}),
        jumlahOrang: Number(f.jumlahOrang),
        catatan: f.catatan,
        tableId: f.tableId === '' ? null : Number(f.tableId),
        mejaTambahan: f.tableId === '' ? [] : f.mejaTambahan.filter((id) => id !== Number(f.tableId)),
        ...(waktuTetap ? {} : { tanggal: f.tanggal, jam: f.jam, durasiMenit: Number(f.durasiMenit) }),
      };
      if (baru && f.pakaiDp) isi.dp = bentukDp(f);
      const res = baru ? await api.post('/reservasi', isi) : await api.put(`/reservasi/${r.uuid}`, isi);
      await onSimpan(res.data);
    } catch (e) {
      setGalat(pesanGalat(e, 'Gagal menyimpan reservasi'));
    } finally {
      setKirim(false);
    }
  };

  // Peringatan kursi: dihitung dari meja utama + tambahan yang dipilih (hanya bila semuanya punya kapasitas).
  const kursiKurang = (() => {
    const dipilih = [f.tableId, ...f.mejaTambahan].filter((id) => id !== '').map((id) => meja.find((m) => m.id === Number(id))).filter(Boolean);
    if (!dipilih.length || dipilih.some((m) => !m.kapasitas)) return '';
    const total = dipilih.reduce((a, m) => a + m.kapasitas, 0);
    return Number(f.jumlahOrang) > total ? `Jumlah tamu (${f.jumlahOrang}) melebihi total kursi ${dipilih.map((m) => m.name).join(' + ')} (${total} kursi)` : '';
  })();

  const dpSah = !f.pakaiDp || (Number(f.dpNominal) > 0 && (f.dpMetode !== 'noncash' || f.dpNonCash) && (f.dpMetode !== 'qris' || Number.isInteger(Number(f.dpNominal))));
  const sah = f.nama.trim() && Number(f.jumlahOrang) >= 1 && f.tanggal && f.jam && (waktuTetap || durasiSah(f.durasiMenit)) && dpSah;

  return (
    <Dialog open onClose={kirim ? undefined : onTutup} fullWidth maxWidth="sm">
      <DialogTitle>{baru ? 'Reservasi baru' : `Ubah reservasi ${r.kode}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            freeSolo
            disabled={!baru}
            options={pelanggan}
            filterOptions={(x) => x}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.nama)}
            renderOption={(props, o) => (
              <li {...props} key={o.uuid}>{o.nama}{o.telepon ? ` · ${o.telepon}` : ''}</li>
            )}
            inputValue={f.nama}
            onInputChange={(_, v, alasan) => {
              if (alasan === 'reset') return;
              // mengetik lagi = bukan pelanggan yang dipilih sebelumnya
              setF((x) => ({ ...x, nama: v, customerUuid: alasan === 'input' ? '' : x.customerUuid }));
            }}
            onChange={(_, o) => {
              if (o && typeof o === 'object') setF((x) => ({ ...x, customerUuid: o.uuid, nama: o.nama, telepon: o.telepon || x.telepon }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Nama pemesan"
                size="small"
                autoFocus
                helperText={baru ? (f.customerUuid ? 'Pelanggan terdaftar dipilih' : 'Ketik untuk mencari pelanggan terdaftar, atau isi nama baru') : undefined}
                inputProps={{ ...params.inputProps, 'data-testid': 'form-nama', maxLength: 100 }}
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <TextField label="Telepon / WhatsApp" size="small" value={f.telepon} onChange={set('telepon')} fullWidth inputProps={{ 'data-testid': 'form-telepon' }} />
            <TextField label="Jumlah orang" size="small" type="number" value={f.jumlahOrang} onChange={set('jumlahOrang')} sx={{ width: 150 }} inputProps={{ min: 1, 'data-testid': 'form-jumlah' }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Tanggal" size="small" type="date" value={f.tanggal} onChange={set('tanggal')} disabled={waktuTetap} InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: 0 }} inputProps={{ 'data-testid': 'form-tanggal' }} />
            <TextField label="Jam" size="small" type="time" value={f.jam} onChange={set('jam')} disabled={waktuTetap} InputLabelProps={{ shrink: true }} sx={{ width: 160, flexShrink: 0 }} inputProps={{ 'data-testid': 'form-jam' }} />
            <FormControl size="small" sx={{ width: 160, flexShrink: 0 }} disabled={waktuTetap}>
              <InputLabel>Durasi</InputLabel>
              <Select
                label="Durasi"
                value={durasiLain ? 'lain' : f.durasiMenit}
                onChange={(e) => {
                  if (e.target.value === 'lain') { setDurasiLain(true); return; }
                  setDurasiLain(false);
                  setF((x) => ({ ...x, durasiMenit: e.target.value }));
                }}
                renderValue={(v) => (v === 'lain' ? `${f.durasiMenit || '…'} menit` : `${v} menit`)}
                data-testid="form-durasi"
              >
                {DURASI.map((d) => <MenuItem key={d} value={d}>{d} menit</MenuItem>)}
                <MenuItem value="lain">Lainnya (atur sendiri)…</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          {durasiLain && !waktuTetap && (
            <TextField
              label={`Durasi (menit, ${DURASI_MIN}-${DURASI_MAKS})`}
              size="small"
              type="number"
              value={f.durasiMenit}
              onChange={set('durasiMenit')}
              error={!durasiSah(f.durasiMenit)}
              helperText={durasiSah(f.durasiMenit) ? `${Math.floor(f.durasiMenit / 60)} jam ${f.durasiMenit % 60} menit` : `Isi ${DURASI_MIN}-${DURASI_MAKS} menit`}
              inputProps={{ min: DURASI_MIN, max: DURASI_MAKS, 'data-testid': 'form-durasi-lain' }}
              autoFocus
            />
          )}
          {waktuTetap && <Alert severity="info">Jam tidak bisa diubah setelah tamu datang.</Alert>}
          <FormControl size="small">
            <InputLabel shrink>Meja</InputLabel>
            <Select
              label="Meja"
              notched
              value={f.tableId}
              onChange={(e) => setF((x) => ({ ...x, tableId: e.target.value, mejaTambahan: e.target.value === '' ? [] : x.mejaTambahan.filter((id) => id !== e.target.value) }))}
              displayEmpty
              data-testid="form-meja"
            >
              <MenuItem value="">Tanpa meja (tentukan nanti)</MenuItem>
              {meja.map((m) => (
                <MenuItem key={m.id} value={m.id} disabled={!m.tersedia}>
                  {m.name}{m.kapasitas ? ` (${m.kapasitas} kursi)` : ''}{!m.tersedia ? ` — terpakai: ${m.bentrok?.ringkas}` : !m.cukup ? ' — kursi kurang' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {f.tableId !== '' && (
            <FormControl size="small">
              <InputLabel shrink>Meja tambahan (rombongan besar)</InputLabel>
              <Select
                multiple
                notched
                label="Meja tambahan (rombongan besar)"
                value={f.mejaTambahan}
                onChange={(e) => setF((x) => ({ ...x, mejaTambahan: e.target.value }))}
                displayEmpty
                renderValue={(v) => (v.length ? (meja.filter((m) => v.includes(m.id)).map((m) => m.name).join(' + ') || `${v.length} meja`) : 'Tidak ada')}
                data-testid="form-meja-tambahan"
              >
                {meja.filter((m) => m.id !== Number(f.tableId)).map((m) => (
                  <MenuItem key={m.id} value={m.id} disabled={!m.tersedia && !f.mejaTambahan.includes(m.id)}>
                    <Checkbox checked={f.mejaTambahan.includes(m.id)} size="small" />
                    <ListItemText primary={`${m.name}${m.kapasitas ? ` (${m.kapasitas} kursi)` : ''}${!m.tersedia ? ` — terpakai: ${m.bentrok?.ringkas}` : ''}`} />
                  </MenuItem>
                ))}
              </Select>
              {kursiKurang && <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }} data-testid="kursi-kurang">{kursiKurang}</Typography>}
            </FormControl>
          )}
          <TextField label="Catatan" size="small" value={f.catatan} onChange={set('catatan')} multiline minRows={1} inputProps={{ maxLength: 255 }} />
          {baru && (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 1.5 }}>
              <FormControlLabel control={<Switch checked={f.pakaiDp} onChange={(e) => setF({ ...f, pakaiDp: e.target.checked })} inputProps={{ 'aria-label': 'Pakai DP' }} />} label="Pakai DP (uang muka)" />
              {f.pakaiDp && <IsianDp nilai={f} setNilai={setF} nonTunai={nonTunai} />}
            </Box>
          )}
          {galat && <Alert severity="error" data-testid="galat-form">{galat}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onTutup} disabled={kirim}>Batal</Button>
        <Button variant="contained" onClick={simpan} disabled={kirim || !sah} data-testid="form-simpan">{kirim ? 'Menyimpan…' : 'Simpan'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const JUDUL_TUTUP = { batal: 'Batalkan reservasi', 'tidak-datang': 'Tandai tidak datang', selesai: 'Selesaikan reservasi' };

/** Batal / tidak datang / selesai: alasan (opsional) dan keputusan DP bila DP sudah diterima. */
const DialogTutup = ({ jenis, r, onTutup, onKirim }) => {
  const [alasan, setAlasan] = useState('');
  const [dpKeputusan, setDpKeputusan] = useState('');
  const [kirim, setKirim] = useState(false);
  const perlu = perluKeputusanDp(r);
  return (
    <Dialog open onClose={kirim ? undefined : onTutup} fullWidth maxWidth="xs">
      <DialogTitle>{JUDUL_TUTUP[jenis]} {r.kode}</DialogTitle>
      <DialogContent>
        <Typography>{r.nama} · {r.jumlahOrang} orang · {r.jam}</Typography>
        {jenis !== 'selesai' && <TextField sx={{ mt: 2 }} fullWidth size="small" label="Alasan (opsional)" value={alasan} onChange={(e) => setAlasan(e.target.value)} inputProps={{ maxLength: 255 }} />}
        {perlu && <PilihanKeputusanDp r={r} nilai={dpKeputusan} setNilai={setDpKeputusan} />}
        {perlu && jenis === 'selesai' && <Alert severity="info" sx={{ mt: 1 }}>DP yang belum dipotong di pembayaran harus diputuskan sebelum reservasi diselesaikan.</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onTutup} disabled={kirim}>Kembali</Button>
        <Button
          variant="contained"
          color={jenis === 'selesai' ? 'primary' : 'error'}
          disabled={kirim || (perlu && !dpKeputusan)}
          onClick={async () => { setKirim(true); await onKirim({ alasan, ...(perlu ? { dpKeputusan } : {}) }); setKirim(false); }}
          data-testid="tutup-kirim"
        >
          {kirim ? 'Memproses…' : 'Ya, lanjutkan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Tambah DP ke reservasi yang belum punya DP (atau ganti QRIS yang belum dibayar). */
const DialogDp = ({ r, param, nonTunai, onTutup, onSelesai }) => {
  const [n, setN] = useState({ dpNominal: r.dp.nominal || '', dpMetode: 'cash', dpNonCash: '' });
  const [kirim, setKirim] = useState(false);
  const [galat, setGalat] = useState('');
  const sah = Number(n.dpNominal) > 0 && (n.dpMetode !== 'noncash' || n.dpNonCash) && (n.dpMetode !== 'qris' || Number.isInteger(Number(n.dpNominal)));
  return (
    <Dialog open onClose={kirim ? undefined : onTutup} fullWidth maxWidth="xs">
      <DialogTitle>DP reservasi {r.kode}</DialogTitle>
      <DialogContent>
        {r.dp.status === 'menunggu_bayar' && <Alert severity="warning" sx={{ mb: 1 }}>DP QRIS sebelumnya belum dibayar. Menyimpan akan menggantinya dengan DP baru.</Alert>}
        <IsianDp nilai={n} setNilai={setN} nonTunai={nonTunai} />
        {galat && <Alert severity="error" sx={{ mt: 1 }}>{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onTutup} disabled={kirim}>Batal</Button>
        <Button
          variant="contained"
          disabled={kirim || !sah}
          onClick={async () => {
            setKirim(true);
            setGalat('');
            try {
              const res = await api.post(`/reservasi/${r.uuid}/dp`, { ...param, ...bentukDp(n) });
              await onSelesai(res.data);
            } catch (e) {
              setGalat(pesanGalat(e, 'Gagal mencatat DP'));
            } finally {
              setKirim(false);
            }
          }}
          data-testid="dp-simpan"
        >
          {kirim ? 'Menyimpan…' : 'Simpan DP'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** DP yang sudah diterima tetapi reservasinya ditutup: dikembalikan atau hangus. */
const DialogKeputusan = ({ r, onTutup, onKirim }) => {
  const [k, setK] = useState('');
  const [catatan, setCatatan] = useState('');
  return (
    <Dialog open onClose={onTutup} fullWidth maxWidth="xs">
      <DialogTitle>Putuskan DP {r.kode}</DialogTitle>
      <DialogContent>
        <PilihanKeputusanDp r={r} nilai={k} setNilai={setK} />
        <TextField sx={{ mt: 2 }} fullWidth size="small" label="Catatan (opsional)" value={catatan} onChange={(e) => setCatatan(e.target.value)} inputProps={{ maxLength: 255 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onTutup}>Batal</Button>
        <Button variant="contained" disabled={!k} onClick={() => onKirim(k, catatan)} data-testid="keputusan-kirim">Simpan keputusan</Button>
      </DialogActions>
    </Dialog>
  );
};

/** QRIS DP: QR ditampilkan dan statusnya dipantau tiap 4 detik sampai lunas. */
const DialogQris = ({ awal, onTutup, onLunas }) => {
  const { r, qris_data: q } = awal;
  const [lunas, setLunas] = useState(false);
  const [gagal, setGagal] = useState('');

  useEffect(() => {
    if (lunas) return undefined;
    const t = setInterval(async () => {
      try {
        const res = await api.get(`/reservasi/${r.uuid}`);
        const dp = res.data?.data?.dp;
        if (dp?.status === 'diterima') { setLunas(true); onLunas?.(); }
        else if (dp?.status === 'tidak_ada') setGagal('QRIS kedaluwarsa atau dibatalkan. Tutup lalu buat DP baru.');
      } catch (e) {
        /* coba lagi pada putaran berikutnya */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [lunas, r.uuid, onLunas]);

  return (
    <Dialog open onClose={onTutup} fullWidth maxWidth="xs">
      <DialogTitle>DP QRIS {rupiah(r.dp.nominal)}</DialogTitle>
      <DialogContent>
        <Typography sx={{ textAlign: 'center', mb: 1 }}>{r.nama} · {r.kode}</Typography>
        {lunas ? <Alert severity="success" data-testid="qris-lunas">DP lunas. Reservasi {r.kode} sudah punya DP.</Alert> : (
          <>
            <QrisKasir qris={{ url: q.generated_image_url, qrString: q.qr_string, isProduction: q.isProduction }} />
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>Minta pelanggan memindai QR ini. Status diperbarui otomatis.</Typography>
          </>
        )}
        {gagal && <Alert severity="warning" sx={{ mt: 1 }}>{gagal}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={onTutup} variant={lunas ? 'contained' : 'text'}>{lunas ? 'Selesai' : 'Tutup'}</Button></DialogActions>
    </Dialog>
  );
};

export default Reservasi;
